import {
  type Condition,
  ConditionOperatorEnum,
  SortOrder,
} from '@repo/api-gw-sdk';

import { collectValueConditions } from '@/types/advanceFilter';

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters -- T only in return is intentional: callers declare the target filter shape so the cast lives here, not scattered across callers.
export const convertQuery = <T extends object = Record<string, unknown>>(
  rootCondition: Condition | undefined
): T | undefined => {
  if (!rootCondition?.conditions?.length) {
    return undefined;
  }

  const conditions = collectValueConditions(rootCondition);
  return conditions.reduce<Record<string, unknown>>((acc, condition) => {
    acc[condition.property!] = {
      [convertOperator(condition.operator)]: condition.value,
    };
    return acc;
  }, {}) as T;
};

// Maps ConditionOperatorEnum values to the TypeScript property names used by the
// SDK filter classes (e.g. CloudProviderFilters, FreeTextFilters). These are the
// `name` fields in each class's attributeTypeMap, NOT the wire-format `baseName`.
// ObjectSerializer.serialize() handles the name→baseName translation when building
// the HTTP body (e.g. '_in' becomes 'in' on the wire for CloudProviderFilters).
// The mock's applyTokenFilters reads the already-serialized body, so it checks
// the baseName keys ('in', 'notIn', 'contains', ...) — not these intermediate names.
const operatorMap: Partial<Record<ConditionOperatorEnum, string>> = {
  [ConditionOperatorEnum.In]: '_in',
  [ConditionOperatorEnum.NotIn]: 'notIn',
  [ConditionOperatorEnum.Contains]: 'contains',
  [ConditionOperatorEnum.NotContains]: 'notContains',
  [ConditionOperatorEnum.ContainsAnyOf]: 'containsAnyOf',
  [ConditionOperatorEnum.ContainsAllOf]: 'containsAllOf',
  [ConditionOperatorEnum.ContainsNoneOf]: 'containsNoneOf',
  [ConditionOperatorEnum.StartsWith]: 'startsWith',
  [ConditionOperatorEnum.NotStartsWith]: 'notStartsWith',
  [ConditionOperatorEnum.EndsWith]: 'endsWith',
  [ConditionOperatorEnum.NotEndsWith]: 'notEndsWith',
};

const convertOperator = (operator: string): string =>
  (operator in operatorMap
    ? operatorMap[operator as ConditionOperatorEnum]
    : undefined) ?? operator;

export const convertSort = <T extends string>(
  sort: string | undefined,
  fieldMap: Record<string, T>
): { field: T; order: SortOrder }[] | undefined => {
  if (!sort) {
    return undefined;
  }
  const [fieldStr, orderStr] = sort.split(':');
  const field = fieldMap[fieldStr];
  if (field === undefined) {
    return undefined;
  }
  const order =
    orderStr?.toLowerCase() === 'desc' ? SortOrder.Desc : SortOrder.Asc;
  return [{ field, order }];
};
